import { RefObject, useEffect, useState } from 'react';

const DEFAULT_MIN_ROWS = 6;
const DEFAULT_ROW_HEIGHT = 38;

type UseSkeletonRowCountProps = {
  containerRef: RefObject<HTMLElement | null>;
  headerRef?: RefObject<HTMLElement | null>;
  minRows?: number;
  rowHeight?: number;
};

const getSkeletonRowCount = ({
  containerHeight,
  headerHeight,
  minRows,
  rowHeight,
}: {
  containerHeight: number;
  headerHeight: number;
  minRows: number;
  rowHeight: number;
}) => {
  const availableHeight = Math.max(containerHeight - headerHeight, 0);

  if (availableHeight <= 0) {
    return minRows;
  }

  return Math.max(minRows, Math.ceil(availableHeight / rowHeight));
};

export const useSkeletonRowCount = ({
  containerRef,
  headerRef,
  minRows = DEFAULT_MIN_ROWS,
  rowHeight = DEFAULT_ROW_HEIGHT,
}: UseSkeletonRowCountProps) => {
  const [skeletonRowCount, setSkeletonRowCount] = useState(minRows);

  useEffect(() => {
    const updateRowCount = () => {
      const containerHeight = containerRef.current?.clientHeight ?? 0;
      const headerHeight = headerRef?.current?.clientHeight ?? 0;

      setSkeletonRowCount(
        getSkeletonRowCount({
          containerHeight,
          headerHeight,
          minRows,
          rowHeight,
        }),
      );
    };

    updateRowCount();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateRowCount();
    });

    const containerElement = containerRef.current;
    const headerElement = headerRef?.current;

    if (containerElement) {
      observer.observe(containerElement);
    }
    if (headerElement) {
      observer.observe(headerElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [containerRef, headerRef, minRows, rowHeight]);

  return skeletonRowCount;
};
